/*
 * Reading design tokens from Canvas.
 *
 * A 2D context resolves no custom properties: `ctx.strokeStyle = 'var(--blue)'`
 * is silently ignored and the stroke ends up black. So canvas code has always
 * been the one place tempted to hardcode a colour — and every hardcoded one is
 * dark-locked, because it cannot see [data-theme].
 *
 * Resolving the token at draw time instead keeps canvas on the same palette as
 * the DOM and tracks a theme switch for free.
 */
/**
 * The current computed value of a custom property, e.g. `token('--blue')`.
 *
 * Resolved live rather than cached: the value changes when the theme flips, and
 * a cache would hand back the previous theme's colour until something
 * invalidated it. getComputedStyle on the root element is cheap enough for a
 * per-frame call, but if a hot loop needs it per-primitive, hoist the lookup to
 * the top of the frame rather than caching across frames.
 */
export function token(name, fallback = '#000000') {
    if (typeof document === 'undefined')
        return fallback;
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}
/**
 * A token as an `rgba()` string at the given alpha — the canvas equivalent of
 * `color-mix(in srgb, var(--x) N%, transparent)`.
 *
 * Accepts the #rgb and #rrggbb forms tokens.css uses. A token already in
 * rgb()/rgba() form is returned unchanged, since it carries its own alpha and
 * re-wrapping it would produce invalid CSS.
 */
export function tokenAlpha(name, alpha, fallback = '#000000') {
    const v = token(name, fallback);
    if (!v.startsWith('#'))
        return v;
    const h = v.slice(1);
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full.slice(0, 6), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
//# sourceMappingURL=canvas-tokens.js.map